import domready from "domready"

import createProgram from "./createProgram";

import vertexShaderSource from "./particle.vert"
import fragmentShaderSource from "./particle.frag"

import m4 from "./m4";

// noinspection ES6UnusedImports
import STYLE from "./style.css"
import createParticleCanvas from "./createParticleCanvas";


const PHI = (1 + Math.sqrt(5)) / 2;
const TAU = Math.PI * 2;
const DEG2RAD_FACTOR = TAU / 360;

const config = {
    width: 0,
    height: 0
};

let gl, canvas, program, particles, matrices, vao;

let a_position;
let a_location;
let a_type;
let a_life;
let u_view;
let u_projection;
let u_texture;

const numVertices = 6;
const numInstances = 2000;

function Tex(texture, width, height)
{
    this.texture = texture;
    this.width = width;
    this.height = height;
}


function createParticlesTexture(width = 1024, height = 1024)
{
    const canvas = createParticleCanvas(width, height);

    const glTex = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, glTex);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
    //gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, true);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, canvas);
    gl.generateMipmap(gl.TEXTURE_2D);
    // gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    // gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    
    return new Tex(glTex, width, height);
}


function setProjectionMatrix()
{
    // Compute the matrix
    const aspect = config.width / config.height;
    const zNear = 0.1;
    const zFar = 2000;
    const projectionMatrix = m4.perspective(70 * TAU / 360, aspect, zNear, zFar);
    gl.uniformMatrix4fv(u_projection, false, projectionMatrix);
}


function resizeCanvasToDisplaySize()
{
    canvas.width = config.width;
    canvas.height = config.height;
    setProjectionMatrix();
}


function render(time)
{
    time *= 0.001; // seconds

    // Tell WebGL how to convert from clip space to pixels
    gl.viewport(0, 0, gl.canvas.width, gl.canvas.height);

    gl.useProgram(program);


    // Compute the camera's matrix using look at.
    const cameraRadius = 30;
    const cameraPosition = [Math.cos(time) * cameraRadius, Math.sin(time * 0.61), Math.sin(time) * cameraRadius];
    const target = [0, 0, 0];
    const up = [0, 1, 0];
    const cameraMatrix = m4.lookAt(cameraPosition, target, up);
    const viewMatrix = m4.inverse(cameraMatrix);
    gl.uniformMatrix4fv(u_view, false, viewMatrix);

    // setup all attributes
    gl.bindVertexArray(vao);

    gl.drawArraysInstanced(
        gl.TRIANGLES,
        0,             // offset
        numVertices,   // num vertices per instance
        numInstances,  // num instances
    );
    requestAnimationFrame(render);
}


function init()
{
    gl = canvas.getContext("webgl2",{
    premultipliedAlpha: false  // Ask for non-premultiplied alpha
});
    if (!gl)
    {
        console.error("No support for WebGL 2");
        return;
    }

    program = createProgram(gl, vertexShaderSource, fragmentShaderSource);

    a_position = gl.getAttribLocation(program, "a_position");
    a_location = gl.getAttribLocation(program, "location");
    a_type = gl.getAttribLocation(program, "type");
    a_life = gl.getAttribLocation(program, "life");
    u_projection = gl.getUniformLocation(program, "projection");
    u_view = gl.getUniformLocation(program, "view");
    u_texture = gl.getUniformLocation(program, "u_texture");

    // Create a vertex array object (attribute state)
    vao = gl.createVertexArray();

    // and make it the one we're currently working with
    gl.bindVertexArray(vao);

    const particleSize = 1;

    const positionBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
    gl.bufferData(
        gl.ARRAY_BUFFER,
        new Float32Array(
            [
                -particleSize,-particleSize,
                -particleSize, particleSize,
                 particleSize, particleSize,

                 particleSize, particleSize,
                 particleSize,-particleSize,
                -particleSize,-particleSize
        ]),
        gl.STATIC_DRAW
    );

    // setup the position attribute
    gl.enableVertexAttribArray(a_position);
    gl.vertexAttribPointer(
        a_position,  // location
        2,            // size (num values to pull from buffer per iteration)
        gl.FLOAT,     // type of data in buffer
        false,        // normalize
        0,            // stride (0 = compute from size and type above)
        0,            // offset in buffer
    );

    // setup random locations
    const locationBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, locationBuffer);
    let array = [];

    for (let i=0; i < numInstances; i++)
    {
        array.push(
            (-1 + Math.random() * 2) * 12,
            (-1 + Math.random() * 2) * 12,
            (-1 + Math.random() * 2) * 12,
        )
    }

    gl.bufferData(
        gl.ARRAY_BUFFER,
        new Float32Array(array),
        gl.STATIC_DRAW
    );

    // set attribute for color
    gl.enableVertexAttribArray(a_location);
    gl.vertexAttribPointer(a_location, 3, gl.FLOAT, false, 0, 0);
    // this line says this attribute only changes for each 1 instance
    gl.vertexAttribDivisor(a_location, 1);

    // setup random particle type
    const typeBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, typeBuffer);
    array = [];

    for (let i=0; i < numInstances; i++)
    {
        array.push(
            Math.round(Math.random()*8)/8
        );
    }

    gl.bufferData(
        gl.ARRAY_BUFFER,
        new Float32Array(array),
        gl.STATIC_DRAW
    );

    // set attribute for color
    gl.enableVertexAttribArray(a_type);
    gl.vertexAttribPointer(a_type, 1, gl.FLOAT, false, 0, 0);
    // this line says this attribute only changes for each 1 instance
    gl.vertexAttribDivisor(a_type, 1);

    // setup lifecycle point
    const lifeBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, lifeBuffer);
    array = [];

    for (let i=0; i < numInstances; i++)
    {
        array.push(
            Math.round(Math.random()*8)/8
        );
    }

    gl.bufferData(
        gl.ARRAY_BUFFER,
        new Float32Array(array),
        gl.STATIC_DRAW
    );

    // set attribute for color
    gl.enableVertexAttribArray(a_life);
    gl.vertexAttribPointer(a_life, 1, gl.FLOAT, false, 0, 0);
    // this line says this attribute only changes for each 1 instance
    gl.vertexAttribDivisor(a_life, 1);

    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA)
}


domready(
    () => {

        canvas = document.getElementById("screen");

        init()

        const width = window.innerWidth;
        const height = window.innerHeight;

        config.width = width;
        config.height = height;


        particles = createParticlesTexture();

        gl.useProgram(program);
        gl.uniform1i(u_texture, particles.texture);
        resizeCanvasToDisplaySize()

        window.addEventListener("resize", () => {

            const width = window.innerWidth;
            const height = window.innerHeight;

            config.width = width;
            config.height = height;

            resizeCanvasToDisplaySize()

        }, true)

        requestAnimationFrame(render)

    }
);
